/**
 * Story 057 — Site (Park) Autocomplete used by Add + Edit modals.
 *
 * Single-select. The Edit modal narrows the editing-gate to
 * `tenant_admin` only (Christian's Q4 lock 2026-05-13); when the actor
 * isn't a tenant_admin, the field is rendered with `aria-disabled` +
 * tooltip — see EditTurbineDialog for the role-narrowed wrapping.
 */
import { Autocomplete, TextField } from "@mui/material";
import type { ReactElement } from "react";
import { useReadOnlyContext } from "@aeriosiq/ui";

export interface SiteOption {
  id: string;
  siteName: string;
}

export interface SiteAutocompleteProps {
  value: string | null;
  options: readonly SiteOption[];
  onChange: (id: string | null) => void;
  disabled?: boolean;
  required?: boolean;
  /** When true, render the input as `aria-disabled` rather than the
   *  fully-disabled form-control state — used by EditTurbineDialog for
   *  the role-narrowed Park field on `project_manager` / `back_office`. */
  ariaDisabled?: boolean;
}

export function SiteAutocomplete({
  value,
  options,
  onChange,
  disabled = false,
  required = false,
  ariaDisabled = false,
}: SiteAutocompleteProps): ReactElement {
  const selected = options.find((o) => o.id === value) ?? null;
  // SB2-CC-03 — when this wrapper is rendered inside a `<ReadOnlyModal>`
  // with `readOnly={true}`, propagate that to MUI's `Autocomplete.readOnly`
  // prop so the popover suppression happens at the MUI level (the
  // ancestor `<fieldset disabled>` alone does not suppress the popper).
  const { readOnly } = useReadOnlyContext();
  return (
    <Autocomplete<SiteOption>
      options={[...options]}
      value={selected}
      disabled={disabled}
      readOnly={readOnly}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      getOptionLabel={(o) => o.siteName}
      onChange={(_e, next) => {
        onChange(next?.id ?? null);
      }}
      renderInput={(params) => {
        /* eslint-disable @typescript-eslint/no-explicit-any */
        const passThrough: any = params;
        return (
          <TextField
            {...passThrough}
            label="Park"
            required={required}
            slotProps={{
              htmlInput: {
                ...params.inputProps,
                "aria-label": "Park",
                ...(required ? { "aria-required": true } : {}),
                ...(ariaDisabled
                  ? { "aria-disabled": true, readOnly: true }
                  : {}),
              },
            }}
            fullWidth
          />
        );
        /* eslint-enable */
      }}
    />
  );
}
